import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Telemetry } from '@/lib/telemetry';

const NOISY_PATTERNS = [
  'hydrat',
  'Loading chunk',
  'ChunkLoadError',
  'Minified React error',
  'ResizeObserver',
];

export function isCrash(error: Error): boolean {
  const normalizedMessage = (error.message ?? '').toLowerCase();
  return !NOISY_PATTERNS.some((pattern) => normalizedMessage.includes(pattern.toLowerCase()));
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);

    const environment = import.meta.env.VITE_ENV;
    const shouldLogToSentry = environment && environment !== 'local';

    if (shouldLogToSentry && isCrash(error)) {
      Telemetry.captureError(error);
    }

    this.categorizeError(error);
  }
  
  categorizeError(error: Error): void {
    // Add error category to state if needed
    let category = 'unknown';
    
    if (error.name === 'ChunkLoadError' || error.message.includes('Loading chunk')) {
      category = 'chunk-load';
    } else if (error.name === 'NetworkError' || error.message.includes('network')) {
      category = 'network';
    } else if (error.name === 'SyntaxError') {
      category = 'syntax';
    } else if (error.name === 'TypeError') {
      category = 'type';
    }
    
    // We could update state with the category if we wanted to show different UIs
    // this.setState({ errorCategory: category });
    
    // Log the category for debugging
    console.info(`Error categorized as: ${category}`);
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Redirect to error page with error details
      return (
        <Navigate 
          to="/error" 
          state={{ error: this.state.error }} 
          replace 
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
